import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

// API base URL - points directly to api subdomain root
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
}

// CSRF token refresh state
let isRefreshingCsrf = false;
let csrfRefreshSubscribers: Array<() => void> = [];

function onCsrfRefreshed(): void {
  csrfRefreshSubscribers.forEach((callback) => callback());
  csrfRefreshSubscribers = [];
}

function addCsrfRefreshSubscriber(callback: () => void): void {
  csrfRefreshSubscribers.push(callback);
}

// Create axios instance with default config
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Important for HTTPOnly cookies
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Request interceptor for CSRF token and impersonation header
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // For mutation requests, ensure we have a CSRF token
    if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-XSRF-TOKEN'] = csrfToken;
      }
    }

    // Add impersonation session header if active (staff build only)
    const impersonationSessionId = getImpersonationSessionId();
    if (impersonationSessionId) {
      config.headers['X-Impersonation-Session'] = impersonationSessionId;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor with CSRF retry
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    // Handle 419 CSRF mismatch with automatic retry
    if (status === 419 && originalRequest && !originalRequest._retry) {
      if (isRefreshingCsrf) {
        // Queue request until CSRF is refreshed
        return new Promise((resolve) => {
          addCsrfRefreshSubscriber(() => {
            resolve(apiClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshingCsrf = true;

      try {
        await getCsrfCookie();
        isRefreshingCsrf = false;
        onCsrfRefreshed();
        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshingCsrf = false;
        csrfRefreshSubscribers = [];
        window.dispatchEvent(new CustomEvent('auth:csrf-mismatch'));
        return Promise.reject(refreshError);
      }
    }

    if (status === 401) {
      // Unauthorized - clear auth and redirect to login
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    } else if (status === 403) {
      // Forbidden - user doesn't have permission
      window.dispatchEvent(new CustomEvent('auth:forbidden'));
    }

    return Promise.reject(error);
  }
);

// Helper to get CSRF token from cookie
function getCsrfToken(): string | null {
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

// Helper to get impersonation session ID from localStorage (staff build only)
function getImpersonationSessionId(): string | null {
  try {
    const stored = localStorage.getItem('impersonation-storage');
    if (stored) {
      const parsed = JSON.parse(stored);
      const session = parsed?.state?.session;
      if (session?.is_active && session?.id) {
        return String(session.id);
      }
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

// Get CSRF cookie from API
export async function getCsrfCookie(): Promise<void> {
  await axios.get(`${API_URL}/auth/csrf`, {
    withCredentials: true,
    timeout: 5000,
  });
}

// Type-safe API methods
export const api = {
  get: <T>(url: string, config?: Parameters<typeof apiClient.get>[1]) =>
    apiClient.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: Parameters<typeof apiClient.post>[2]) =>
    apiClient.post<T>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: Parameters<typeof apiClient.put>[2]) =>
    apiClient.put<T>(url, data, config).then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: Parameters<typeof apiClient.patch>[2]) =>
    apiClient.patch<T>(url, data, config).then((res) => res.data),

  delete: <T>(url: string, config?: Parameters<typeof apiClient.delete>[1]) =>
    apiClient.delete<T>(url, config).then((res) => res.data),
};

export default api;
